<div>
  <div class="container-fluid">
    <!-- Back Button -->
    <div class="mb-3">
      <button type="button" class="btn btn-outline-secondary" (click)="cancel()">
        <i class="bi bi-arrow-left me-2"></i>
        {{ isEditMode ? 'Back to Drone Details' : 'Back to Drone Inventory' }}
      </button>
    </div>

    <!-- Loading State -->
    <div *ngIf="loadingDrone" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="text-muted mt-3">Loading drone details...</p>
    </div>

    <div *ngIf="!loadingDrone" class="row">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="adn-icon adn-icon-drone-solid me-2"></i>
              {{ pageTitle }}
            </h5>
          </div>
          <div class="card-body">
            <!-- Error Message -->
            <div *ngIf="errorMessage" class="alert alert-danger" role="alert">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ errorMessage }}
            </div>

            <!-- Success Message -->
            <div *ngIf="successMessage" class="alert alert-success" role="alert">
              <i class="bi bi-check-circle-fill me-2"></i>
              {{ successMessage }}
            </div>

            <form [formGroup]="droneForm" (ngSubmit)="onSubmit()">
              <!-- Basic Information -->
              <div class="border-bottom pb-2 mb-3">
                <h6 class="fw-semibold mb-1">Basic Information</h6>
                <p class="text-muted small mb-0">Drone identification and model details</p>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label for="manufacturerId" class="form-label">Manufacturer <span class="text-danger">*</span></label>
                  <select
                    class="form-select"
                    id="manufacturerId"
                    formControlName="manufacturerId"
                    [class.is-invalid]="droneForm.get('manufacturerId')?.invalid && droneForm.get('manufacturerId')?.touched"
                  >
                    <option value="">Select manufacturer...</option>
                    <option *ngFor="let mfr of manufacturers" [value]="mfr.id">{{ mfr.name }}</option>
                  </select>
                  <div *ngIf="droneForm.get('manufacturerId')?.invalid && droneForm.get('manufacturerId')?.touched" class="invalid-feedback">
                    Manufacturer is required
                  </div>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="modelId" class="form-label">Model <span class="text-danger">*</span></label>
                  <select
                    class="form-select"
                    id="modelId"
                    formControlName="modelId"
                    [class.is-invalid]="droneForm.get('modelId')?.invalid && droneForm.get('modelId')?.touched"
                    [disabled]="!droneForm.get('manufacturerId')?.value"
                  >
                    <option value="">{{ droneForm.get('manufacturerId')?.value ? 'Select model...' : 'Select manufacturer first' }}</option>
                    <option *ngFor="let model of availableModels" [value]="model.id">{{ model.modelName }}</option>
                  </select>
                  <div *ngIf="droneForm.get('modelId')?.invalid && droneForm.get('modelId')?.touched" class="invalid-feedback">
                    Model is required
                  </div>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="name" class="form-label">Drone Name <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control"
                    id="name"
                    formControlName="name"
                    placeholder="e.g., ADN-DJI-001"
                    [class.is-invalid]="droneForm.get('name')?.invalid && droneForm.get('name')?.touched"
                  />
                  <div *ngIf="droneForm.get('name')?.invalid && droneForm.get('name')?.touched" class="invalid-feedback">
                    Drone name is required
                  </div>
                  <div class="form-text">Internal identifier for this drone</div>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="status" class="form-label">Status <span class="text-danger">*</span></label>
                  <select
                    class="form-select"
                    id="status"
                    formControlName="status"
                    [class.is-invalid]="droneForm.get('status')?.invalid && droneForm.get('status')?.touched"
                  >
                    <option *ngFor="let status of statuses" [value]="status.value">{{ status.label }}</option>
                  </select>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="serialNumber" class="form-label">Serial Number <span class="text-danger">*</span></label>
                  <input
                    type="text"
                    class="form-control font-monospace"
                    id="serialNumber"
                    formControlName="serialNumber"
                    placeholder="e.g., SN1ABC2DEF3"
                    [class.is-invalid]="droneForm.get('serialNumber')?.invalid && droneForm.get('serialNumber')?.touched"
                  />
                  <div *ngIf="droneForm.get('serialNumber')?.invalid && droneForm.get('serialNumber')?.touched" class="invalid-feedback">
                    Serial number is required
                  </div>
                  <div class="form-text">Manufacturer's serial number</div>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="packagingSerialNumber" class="form-label">Packaging Serial Number</label>
                  <input
                    type="text"
                    class="form-control font-monospace"
                    id="packagingSerialNumber"
                    formControlName="packagingSerialNumber"
                    placeholder="e.g., PKG1234567"
                  />
                  <div class="form-text">Serial number from packaging (if different)</div>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="version" class="form-label">Version</label>
                  <input
                    type="text"
                    class="form-control"
                    id="version"
                    formControlName="version"
                    placeholder="e.g., v2.5 or Rev A"
                  />
                  <div class="form-text">Hardware version or revision</div>
                </div>

                <div class="col-md-12 mb-3">
                  <label for="inventoryNote" class="form-label">Inventory Location Note</label>
                  <textarea
                    id="inventoryNote"
                    class="form-control"
                    rows="3"
                    formControlName="inventoryNote"
                    placeholder="Add any notes about where this drone is stored..."></textarea>
                </div>
              </div>

              <!-- Registration & Identification -->
              <div class="border-bottom pb-2 mb-3 mt-4">
                <h6 class="fw-semibold mb-1">
                  <i class="bi bi-card-checklist me-2"></i>
                  Registration & Identification
                </h6>
                <p class="text-muted small mb-0">FAA registration and remote ID details</p>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label for="nNumber" class="form-label">FAA N-Number</label>
                  <input
                    type="text"
                    class="form-control font-monospace"
                    id="nNumber"
                    formControlName="nNumber"
                    placeholder="e.g., N12345"
                  />
                  <div class="form-text">FAA registration number (required for assignment)</div>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="nNumberActiveDate" class="form-label">N-Number Active Date</label>
                  <input
                    type="date"
                    class="form-control"
                    id="nNumberActiveDate"
                    formControlName="nNumberActiveDate"
                  />
                  <div class="form-text">Date the N-Number became active</div>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="remoteId" class="form-label">Remote ID</label>
                  <input
                    type="text"
                    class="form-control font-monospace"
                    id="remoteId"
                    formControlName="remoteId"
                    placeholder="e.g., RID-ABC123"
                  />
                  <div class="form-text">Remote ID broadcast identifier</div>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="remoteBroadcastModule" class="form-label">Remote Broadcast Module</label>
                  <input
                    type="text"
                    class="form-control font-monospace"
                    id="remoteBroadcastModule"
                    formControlName="remoteBroadcastModule"
                    placeholder="e.g., DJI RID Module v1"
                  />
                  <div class="form-text">External broadcast module identifier</div>
                </div>
              </div>

              <!-- Actions -->
              <div class="d-flex gap-2 mt-4">
                <button
                  type="submit"
                  class="btn btn-primary"
                  [disabled]="loading || droneForm.invalid"
                >
                  <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
                  <i *ngIf="!loading" class="adn-icon adn-icon-drone-solid me-2"></i>
                  {{ submitButtonText }}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  (click)="cancel()"
                  [disabled]="loading"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Sidebar Info -->
      <div class="col-lg-4">
        <div class="card mt-4 mt-lg-0">
          <div class="card-body">
            <h6 class="fw-semibold mb-3">
              <i class="bi bi-info-circle text-primary me-2"></i>
              Drone Registration Info
            </h6>
            <div class="mb-3">
              <strong class="d-block small mb-1">FAA N-Number</strong>
              <p class="text-muted small mb-0">
                FAA registration number for the drone. Required before a drone can be assigned to an organization.
              </p>
            </div>
            <div class="mb-3">
              <strong class="d-block small mb-1">Remote ID</strong>
              <p class="text-muted small mb-0">
                As of 2023, drones must broadcast Remote ID. This can be built-in or via an external module.
              </p>
            </div>
            <div class="mb-3">
              <strong class="d-block small mb-1">Serial Numbers</strong>
              <p class="text-muted small mb-0">
                The serial number can typically be found on the drone body,
                battery compartment, or in the manufacturer's app.
              </p>
            </div>
            <div>
              <strong class="d-block small mb-1">Status Definitions</strong>
              <ul class="text-muted small mb-0 ps-3">
                <li><strong>Available:</strong> Ready for assignment</li>
                <li><strong>Assigned:</strong> Currently assigned to an organization</li>
                <li><strong>Maintenance:</strong> Undergoing repairs/service</li>
                <li><strong>Retired:</strong> No longer in service</li>
              </ul>
            </div>
          </div>
        </div>

        <div *ngIf="isEditMode" class="card mt-3">
          <div class="card-body">
            <h6 class="fw-semibold mb-3 text-danger">
              <i class="bi bi-exclamation-triangle me-2"></i>
              Danger Zone
            </h6>
            <p class="text-muted small mb-3">
              Permanently remove this drone from inventory. This action cannot be undone.
            </p>
            <button type="button" class="btn btn-outline-danger btn-sm" disabled>
              <i class="bi bi-trash me-2"></i>
              Delete Drone
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
